import * as Creepts from "endless-siege-engine";
import { MineActor } from "../turret-actors/MineActor";
import { GluePool } from "../turret-actors/GluePool";
import { EnemyActor } from "../enemy-actors/EnemyActor";
import { TurretActor } from "../turret-actors/TurretActor";
import { Board } from "./Board";
import { GameConstants } from "../../../GameConstants";
import { ArrowActor } from "../turret-actors/ArrowActor";
import { GameVars } from "../../../GameVars";
import { ProjectileTurretActor } from "../turret-actors/ProjectileTurretActor";
import { LaserTurretActor } from "../turret-actors/LaserTurretActor";
import { LaserBeam } from "../turret-actors/LaserBeam";
import { LaunchTurretActor } from "../turret-actors/LaunchTurretActor";
import { MortarActor } from "../turret-actors/MortarActor";
import { GlueTurretActor } from "../turret-actors/GlueTurretActor";
import { BattleManager } from "../BattleManager";
import { PauseMenu } from "../PauseMenu";
import { GlueBulletActor } from "../turret-actors/GlueBulletActor";
import { AudioManager } from "../../../AudioManager";
import { RoundCompletedLayer } from "../RoundCompletedLayer";
import { Walls } from "./Walls";
import { SmokeEffect } from "../SmokeEffect";
import { BattleScene } from "../BattleScene";
import { TutorialManager } from "../TutorialManager";
import { ArrowActorsPool } from "../turret-actors/ArrowActorsPool";
import { EnemyActorsPool } from "../enemy-actors/EnemyActorsPool";

export class BoardContainer extends Phaser.GameObjects.Container {

    public static currentInstance: BoardContainer;

    public board: Board;
    public enemyActorsPool: EnemyActorsPool;

    private enemyActors: EnemyActor[];
    private turretActors: TurretActor[];
    private arrowActors: ArrowActor[];
    private glueBulletActors: GlueBulletActor[];
    private mortarActors: MortarActor[];
    private mineActors: MineActor[];
    private rangeCircles: Phaser.GameObjects.Image[];
    private pauseMenu: PauseMenu;
    private roundCompletedLayer: RoundCompletedLayer;
    private waterContainer: Phaser.GameObjects.Container;
    private pointerContainer: Phaser.GameObjects.Container;
    private glueCirclesContainer: Phaser.GameObjects.Container;
    private circlesContainer: Phaser.GameObjects.Container;
    private shotsContainer: Phaser.GameObjects.Container;
    private prePosition: Phaser.GameObjects.Graphics;
    private gluePools: GluePool[];
    private walls: Walls;
    private lastPrePositionTurret: {c: number, r: number};
    private tutorialPosition: {c: number, r: number};
    private handCursor: Phaser.GameObjects.Image;
    private tutorialTurretContainer: Phaser.GameObjects.Container;
    private arrowActorsPool: ArrowActorsPool;

    constructor(scene: Phaser.Scene) {

        super(scene);

        BoardContainer.currentInstance = this;

        this.x = GameConstants.GAME_WIDTH / 2;
        this.y = GameConstants.GAME_HEIGHT / 2 - GameConstants.CELLS_SIZE / 2;

        this.setScale(GameVars.scaleCorrectionFactor, GameVars.scaleCorrectionFactor * GameVars.scaleY);

        this.createAnimations();

        this.enemyActors = [];
        this.turretActors = [];
        this.arrowActors = [];
        this.glueBulletActors = [];
        this.mortarActors = [];
        this.mineActors = [];
        this.rangeCircles = [];
        this.gluePools = [];

        this.lastPrePositionTurret = null;

        this.arrowActorsPool = new ArrowActorsPool(this.scene, 5);
        this.enemyActorsPool = new EnemyActorsPool(this.scene, 20, 20, 5, 5);

        this.board = new Board(this.scene);
        this.add(this.board);

        if (GameConstants.SHOW_DEBUG_GEOMETRY) {
            this.drawDebugGeometry();
        }

        this.waterContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.waterContainer);

        this.pointerContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.pointerContainer);

        this.glueCirclesContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.glueCirclesContainer);

        this.pointerContainer.setSize(GameConstants.GAME_WIDTH * 4, GameConstants.GAME_HEIGHT * 4);
        this.pointerContainer.setInteractive();
        this.pointerContainer.on("pointerdown", () => { this.onPointerDown(); });

        this.board.actorsContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.board.actorsContainer);

        this.shotsContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.shotsContainer);

        this.circlesContainer = new Phaser.GameObjects.Container(this.scene);
        this.board.add(this.circlesContainer);

        this.prePosition = new Phaser.GameObjects.Graphics(this.scene);
        this.prePosition.fillStyle(0xffffff, .5);
        this.prePosition.fillRect(-30, -30, 60, 60);
        this.prePosition.visible = false;
        this.board.add(this.prePosition);

        this.addObstacles();

        this.walls = new Walls(this.scene);
        this.board.add(this.walls);

        if (this.board.portalFront) {
            this.board.bringToTop(this.board.portal);
        }
    }

    public updatePauseMenu(): void {
        if (this.pauseMenu) {
            this.pauseMenu.update();
        }
    }

    public update(time: number, delta: number): void {
        this.enemyActors.forEach(function (enemy) {
            enemy.update(time, delta);
        }); 

        this.turretActors.forEach(function (tower) {
            tower.update(time, delta);
        }); 

        this.arrowActors.forEach(function (bullet) {
            bullet.update(time, delta);
        }); 

        this.glueBulletActors.forEach(function (bullet) {
            bullet.update(time, delta);
        }); 

        this.mortarActors.forEach(function (mortar) {
            mortar.update(time, delta);
        }); 
    }

    public markCellOnTutorial(): void {

        // ENCONTRAR LA CELDA ADECUADA
        const pathCell = GameVars.enemiesPathCells[6];
        const surroundingCells = [
            {c: -1, r: -1}, {c: 0, r: -1}, {c: 1, r: -1},
            {c: -1, r: 0}, {c: 1, r: 0},
            {c: -1, r: 1}, {c: 0, r: 1}, {c: 1, r: 1},
        ];

        let c: number;
        let r: number;

        for (let i = 0; i < surroundingCells.length; i ++) {

            c = pathCell.c + surroundingCells[i].c;
            r = pathCell.r + surroundingCells[i].r;

            let canBeBuiltUpon = true;

            if (c < 0 || r < 0) {
                canBeBuiltUpon = false;
            } else {

                // que no sea una celda no edificable
                for (let j = 0; j < GameVars.plateausCells.length; j ++) {
                    if (c === GameVars.plateausCells[j].c && r === GameVars.plateausCells[j].r) {
                        canBeBuiltUpon = false;
                        break;
                    }
                }

                // que no sea una celda del camino
                for (let j = 0; j < GameVars.enemiesPathCells.length; j ++) {
                    if (c === GameVars.enemiesPathCells[j].c && r === GameVars.enemiesPathCells[j].r) {
                        canBeBuiltUpon = false;
                        break;
                    }
                }
            }

            if (canBeBuiltUpon) {
                break;
            }
        }

        this.tutorialPosition = {c: c, r: r};

        // ponemos una torreta de ballesta con alfa bajado(
        this.tutorialTurretContainer = new Phaser.GameObjects.Container(this.scene, (this.tutorialPosition.c + .5) * GameConstants.CELLS_SIZE, (this.tutorialPosition.r + .5) * GameConstants.CELLS_SIZE - 8);
        this.tutorialTurretContainer.setScale(.8);
        this.tutorialTurretContainer.alpha = .45; 
        this.board.add(this.tutorialTurretContainer);

        const turretBase = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_4", "archer_1_base");
        this.tutorialTurretContainer.add(turretBase);

        const turretCannon = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_4", "archer_1_top");
        turretCannon.angle = -90;
        this.tutorialTurretContainer.add(turretCannon);

        // ponemos en cursor del dedo
        this.handCursor = new Phaser.GameObjects.Image(this.scene, (this.tutorialPosition.c + .5) * GameConstants.CELLS_SIZE + 15, (this.tutorialPosition.r + .5) * GameConstants.CELLS_SIZE, "texture_atlas_4", "hand_cursor");
        this.handCursor.setOrigin(.05, .2);
        this.board.add(this.handCursor);

        this.scene.tweens.add({
            targets: this.handCursor,
            scaleX: 1.1,
            scaleY: 1.1,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 750,
            yoyo: true,
            repeat: -1
        });
    }

    public onFirstEnemyKilled(): void {

        this.turretActors[0].addHandCursor();
    }

    public sortActors(): void {
        
        this.board.actorsContainer.sort("y");
    }

    public addEnemy(enemy: Creepts.Enemy, position: {r: number, c: number}): void {

        let enemyActor = this.enemyActorsPool.getEnemy(enemy, position);
        
        if (enemyActor) {
            this.board.actorsContainer.add(enemyActor);
            this.enemyActors.push(enemyActor);
        }

        this.board.newPortalGlare();
    }

    public removeEnemy(id: number): void {

        let i: number;
        for (i = 0; i < this.enemyActors.length; i ++) {
            if (this.enemyActors[i].id === id) {
               break; 
            }
        }

        const enemy = this.enemyActors[i];

        if (enemy) {
            this.enemyActors.splice(i, 1);
            this.enemyActorsPool.onEnemyRemovedFromBoard(enemy);
            this.board.actorsContainer.remove(enemy);
        }
    }

    public addTurret(type: string, position: {r: number, c: number}): void {

        this.prePosition.visible = false;

        if ( position.r < 0 || position.c < 0 || position.r >= GameVars.currentMapData.size.r || position.c >= GameVars.currentMapData.size.c) {
            return;
        }

        for (let i = 0; i < GameVars.enemiesPathCells.length; i++) {
            if (position.c === GameVars.enemiesPathCells[i].c && position.r === GameVars.enemiesPathCells[i].r) {
                return;
            }
        }

        for (let i = 0; i < this.turretActors.length; i++) {
            if (position.c === this.turretActors[i].turret.position.c && position.r === this.turretActors[i].turret.position.r) {
                return;
            }
        }

        let isOnPlateau = true;

        if (GameVars.plateausCells.length !== 0) {
            for (let i = 0; i < GameVars.plateausCells.length; i++) {
                if (GameVars.plateausCells[i].c === position.c && GameVars.plateausCells[i].r === position.r) {
                    isOnPlateau = false;
                    break;
                }
            }
        }

        if (!isOnPlateau) {
            return;
        }

        if (!GameVars.gameData.tutorialSeen) {
            if (!(this.tutorialPosition.c === position.c && this.tutorialPosition.r === position.r)) {
                return;
            } else {
                
                this.tutorialTurretContainer.destroy();
                this.handCursor.destroy();
                TutorialManager.onTurretPlaced();
            }
        }

        let turretActor: TurretActor;
        let turret = BattleManager.addTurret(type, position);

        if (!turret) {
            return;
        }

        switch (type) {

            case Creepts.GameConstants.TURRET_PROJECTILE:
                turretActor = new ProjectileTurretActor(this.scene, position, turret);
                break;
            case Creepts.GameConstants.TURRET_LASER:
                turretActor = new LaserTurretActor(this.scene, position, turret);
                break;
            case Creepts.GameConstants.TURRET_LAUNCH:
                turretActor = new LaunchTurretActor(this.scene, position, turret);
                break;
            case Creepts.GameConstants.TURRET_GLUE:
                turretActor = new GlueTurretActor(this.scene, position, turret);
                break;
            default:
        }
        
        this.board.actorsContainer.add(turretActor);
        this.turretActors.push(turretActor);

        turretActor.onAddedToBoardContainer();

        AudioManager.playSoundEffect("turret_planted");
    }

    public onTurretSold(id: number): void {

        BattleScene.currentInstance.hideTurretMenu();
        this.hideRangeCircles();

        let i: number;
        for (i = 0; i < this.turretActors.length; i ++) {
            if (this.turretActors[i].id === id) {
               break; 
            }
        }

        const turret = this.turretActors[i];

        if (turret) {
            
            const smokeEffect = new SmokeEffect(this.scene);
            smokeEffect.x = turret.x;
            smokeEffect.y = turret.y;
            this.board.actorsContainer.add(smokeEffect);

            this.turretActors.splice(i, 1);
            turret.sold();
        }
    }

    public addBullet(bullet: Creepts.Bullet, projectileTurret: Creepts.ProjectileTurret): void {

        const projectileTurretActor = <ProjectileTurretActor> this.getTurretActorByID(projectileTurret.id);
        projectileTurretActor.shootArrow();

        const arrowActor = this.arrowActorsPool.getArrow(bullet);
        this.shotsContainer.add(arrowActor);

        this.arrowActors.push(arrowActor);
    }

    public addGlueBullet(bullet: Creepts.GlueBullet, projectileTurret: Creepts.GlueTurret): void {

        const glueTurretActor = <GlueTurretActor> this.getTurretActorByID(projectileTurret.id);
        glueTurretActor.shootGlue();

        const bulletActor = new GlueBulletActor(this.scene, bullet);
        this.shotsContainer.add(bulletActor);

        this.glueBulletActors.push(bulletActor);
    }

    public addLaserBeam (laserTurret: Creepts.LaserTurret, enemies: Creepts.Enemy[]): void {

        const laserTurretActor = <LaserTurretActor> this.getTurretActorByID(laserTurret.id);

        let enemyActors = [];

        for (let i = 0; i < enemies.length; i++) {
            enemyActors.push(this.getEnemyActorByID(enemies[i].id));
        }

        laserTurretActor.shootLaser(enemyActors);

        const laserBeam = new LaserBeam(this.scene, laserTurretActor, enemyActors, laserTurret.grade);
        this.shotsContainer.add(laserBeam);
    }

    public addMortar(mortar: Creepts.Mortar, launchTurret: Creepts.LaunchTurret): void {

        const launchTurretActor = <LaunchTurretActor> this.getTurretActorByID(launchTurret.id);
        launchTurretActor.shootMortar();

        const mortarActor = new MortarActor(this.scene, mortar);
        this.shotsContainer.add(mortarActor);

        this.mortarActors.push(mortarActor);
    }

    public addMine(mine: Creepts.Mine, launchTurret: Creepts.LaunchTurret): void {

        const launchTurretActor = <LaunchTurretActor> this.getTurretActorByID(launchTurret.id);
        launchTurretActor.shootMine();

        const mineActor = new MineActor(this.scene, mine, launchTurretActor);
        this.glueCirclesContainer.add(mineActor);

        this.mineActors.push(mineActor);
    }

    public addGlue(glue: Creepts.Glue, glueTurret: Creepts.GlueTurret): void {

        const glueTurretActor = <GlueTurretActor> this.getTurretActorByID(glueTurret.id);
        glueTurretActor.shootGlue();

        const gluePool = new GluePool(this.scene, glueTurretActor, glue);
        this.glueCirclesContainer.add(gluePool);
        this.glueCirclesContainer.sendToBack(gluePool);
        this.gluePools.push(gluePool);
    }

    public onGlueConsumed(glue: Creepts.Glue): void {

        let gluePool: GluePool = null;

        for (let i = 0; i < this.gluePools.length; i++) {

            if (glue.id === this.gluePools[i].id) {
                gluePool = this.gluePools[i];
                this.gluePools.splice(i, 1);
                gluePool.destroy();
                break;
            }
        }
    }

    public onEnemyHit(enemy: Creepts.Enemy, impactType: string): void {
        
        let enemyActor: EnemyActor = this.getEnemyActorByID(enemy.id);

        if (enemyActor && !enemyActor.isDead) {
            enemyActor.hit(impactType);
        }
    }

    public onEnemyGlueHit(enemy: Creepts.Enemy): void {
        
        let enemyActor: EnemyActor = this.getEnemyActorByID(enemy.id);

        if (enemyActor && !enemyActor.isDead) {
            enemyActor.glueHit();
        }
    }

    public onEnemyKilled(enemy: Creepts.Enemy): void {

        let enemyActor: EnemyActor = this.getEnemyActorByID(enemy.id);

        if (enemyActor) {
            enemyActor.die();
        }
    }

    public teleportEnemy(enemy: Creepts.Enemy, glueTurret: Creepts.GlueTurret): void {

        let enemyActor: EnemyActor = this.getEnemyActorByID(enemy.id);

        if (enemyActor && !enemyActor.isDead) {
            enemyActor.teleport(glueTurret);
            AudioManager.playSoundEffect("t3_teleport");

            let teleport = new Phaser.GameObjects.Sprite(this.scene, enemyActor.x, enemyActor.y - 10, "texture_atlas_3");
            this.scene.add.existing(teleport);
            this.board.add(teleport);

            teleport.anims.play("teleport_in");

            teleport.on("animationcomplete", () => {
                teleport.destroy();
            }, this);
        } 
    }

    public teleportEnd(enemy: EnemyActor): void {

        let teleport = new Phaser.GameObjects.Sprite(this.scene, enemy.x, enemy.y - 10, "texture_atlas_3");
        this.scene.add.existing(teleport);
        this.board.add(teleport);

        teleport.anims.play("teleport_out");

        teleport.on("animationcomplete", () => {
            teleport.destroy();
        }, this);
    }

    public removeBullet(bullet: Creepts.Bullet): void {

        let arrowActor: ArrowActor = null;

        for (let i = 0; i < this.arrowActors.length; i ++) {

            if (this.arrowActors[i].bullet.id === bullet.id) {
                arrowActor = this.arrowActors[i];
                break;
            }
        }

        if (arrowActor) {

            const i = this.arrowActors.indexOf(arrowActor);
            this.arrowActors.splice(i, 1);
            
            this.shotsContainer.remove(arrowActor);
            this.arrowActorsPool.onArrowRemovedFromBoard(arrowActor);
        }
    }

    public removeGlueBullet(bullet: Creepts.GlueBullet): void {

        let bulletActor: GlueBulletActor = null;

        for (let i = 0; i < this.glueBulletActors.length; i ++) {

            if (this.glueBulletActors[i].glueBullet.id === bullet.id) {
                bulletActor = this.glueBulletActors[i];
                break;
            }
        }

        if (bulletActor) {
            const i = this.glueBulletActors.indexOf(bulletActor);
            this.glueBulletActors.splice(i, 1);
            bulletActor.destroy();
        }
    }

    public detonateMortar(mortar: Creepts.Mortar): void {

        let mortarActor: MortarActor = null;

        for (let i = 0; i < this.mortarActors.length; i ++) {

            if (this.mortarActors[i].mortar.id === mortar.id) {
                mortarActor = this.mortarActors[i];
                break;
            }
        }

        if (mortarActor) {
            mortarActor.detonate();
        }
    }

    public detonateMine(mine: Creepts.Mine): void {

        let mineActor: MineActor = null;

        for (let i = 0; i < this.mineActors.length; i ++) {

            if (this.mineActors[i].mine.id === mine.id) {
                mineActor = this.mineActors[i];
                break;
            }
        }

        if (mineActor) {
            mineActor.detonate();
        }
    }

    public removeMortar(mortarActor: MortarActor): void {

        const i = this.mortarActors.indexOf(mortarActor);
        this.mortarActors.splice(i, 1);
        mortarActor.destroy();
    }

    public removeMine(mineActor: MineActor): void {

        const i = this.mineActors.indexOf(mineActor);
        this.mineActors.splice(i, 1);
        mineActor.destroy();
    }
    
    public upgradeTurret(id: number): void {
        
        const turretActor = this.getTurretActorByID(id);
        turretActor.upgrade();
    }

    public improveTurret(id: number): void {
        
        const turretActor = this.getTurretActorByID(id);
        turretActor.improve();
    }
    
    public getTurretActorByID(id: number): TurretActor {

        let turretActor = null;

        for (let i = 0; i < this.turretActors.length; i ++) {
            if (this.turretActors[i].id === id) {
                turretActor = this.turretActors[i];
                break;
            }
        }

        return turretActor;
    }

    public getEnemyActorByID(id: number): EnemyActor {

        let enemy = null;

        for (let i = 0; i < this.enemyActors.length; i ++) {
            if (this.enemyActors[i].id === id) {
                enemy = this.enemyActors[i];
                break;
            }
        }

        return enemy;
    }

    public createRangeCircle(range: number, x: number, y: number): Phaser.GameObjects.Image {

        const rangeCircle = new Phaser.GameObjects.Image(this.scene, x, y, "texture_atlas_4", "shoot_range");
        rangeCircle.setScale((range * 2) / rangeCircle.width);
        rangeCircle.visible = false;
        this.circlesContainer.add(rangeCircle);

        this.rangeCircles.push(rangeCircle);

        return rangeCircle;
    }


    public hideRangeCircles(): void {

        for (let i = 0; i < this.rangeCircles.length; i ++) {
            this.rangeCircles[i].visible = false;
        }

        this.board.hideTurretFrame();
    }
    
    public showPauseMenu(): void {

        if (!this.pauseMenu) {
            BattleManager.pause();
            this.pauseMenu = new PauseMenu(this.scene);
            BattleScene.currentInstance.add.existing(this.pauseMenu);
            this.pauseMenu.setScale(this.scaleX, this.scaleY);
            this.pauseMenu.x = this.x;
            this.pauseMenu.y = this.y - 150;
            this.hideRangeCircles();
            BattleScene.currentInstance.hideTurretMenu();
        } else {
            this.hidePauseMenu();
        }
    }

    public hidePauseMenu(): void {

        if (this.pauseMenu) {
            BattleManager.resume();
            this.pauseMenu.destroy();
            this.pauseMenu = null;
            this.pauseMenu = null;
        }
    }

    public onGameOver(): void {

        if (this.pauseMenu) {
            this.hidePauseMenu();
        }
    }

    public showRoundCompletedLayer(): void {

        if (GameVars.timeStepFactor === 8 || GameVars.autoSendWave || !GameVars.gameData.tutorialSeen) {
            return;
        } 

        this.roundCompletedLayer = new RoundCompletedLayer(this.scene);
        this.add(this.roundCompletedLayer);

        this.showTurretsLevelLabel();
    }

    public hideRoundCompletedLayer(): void {

        this.remove(this.roundCompletedLayer);
        this.roundCompletedLayer = null;
    }

    public setTurretPrePosition(c: number, r: number): void {
        
        if (!this.prePosition.visible) {
            this.prePosition.visible = true;
        }

        let canBeAdded = true;

        if (c < 0 || c >= this.board.boardMatrix[0].length || r < 0 || r >= this.board.boardMatrix.length) {
            canBeAdded = false;
        } else {
            if (this.board.boardMatrix[r][c] === 1) {
                canBeAdded = false;
            }
        }

        for (let i = 0; i < this.turretActors.length; i++) {

            if (this.turretActors[i].turret.position.c === c && this.turretActors[i].turret.position.r === r) {
                canBeAdded = false;
            }
        }

        for (let i = 0; i < GameVars.plateausCells.length; i++) {

            if (GameVars.plateausCells[i].c === c && GameVars.plateausCells[i].r === r) {
                canBeAdded = false;
            }
        }

        if (!GameVars.gameData.tutorialSeen && !(this.tutorialPosition.c === c && this.tutorialPosition.r === r)) {
            canBeAdded = false;
        }

        this.prePosition.clear();

        if (canBeAdded) {

            this.prePosition.fillStyle(0xffffff, .5);
            this.prePosition.fillRect(-30, -30, 60, 60);

            if (this.lastPrePositionTurret !== null && (this.lastPrePositionTurret.c !== c || this.lastPrePositionTurret.r !== r)) {
                AudioManager.playSoundEffect("drag_over_buildable_cell");
            }

        } else {
            
            this.prePosition.fillStyle(0xff0000, .5);
            this.prePosition.fillRect(-30, -30, 60, 60);

            if (this.lastPrePositionTurret !== null && (this.lastPrePositionTurret.c !== c || this.lastPrePositionTurret.r !== r)) {
                AudioManager.playSoundEffect("drag_over_non_buildable_cell");
            }
        }

        this.lastPrePositionTurret = {c: c, r: r};

        this.prePosition.setPosition(GameConstants.CELLS_SIZE * c + GameConstants.CELLS_SIZE / 2, GameConstants.CELLS_SIZE * r + GameConstants.CELLS_SIZE / 2);
    }

    public showTurretsLevelLabel(): void {
        for (let i = 0; i < this.turretActors.length; i++) {
            this.turretActors[i].showLevel();
        }
    }

    public tutorialTurretUpgraded(): void {
        if (!TutorialManager.isTurretFirstTimeUpgraded) {
            this.turretActors[0].level = 1;
        }
        ++this.turretActors[0].level;
        this.turretActors[0].hideLevel();
        this.turretActors[0].showLevel();
    }
    
    public hideTurretsLevelLabel(): void {

        if (GameVars.semiPaused) {

            this.showTurretsLevelLabel();
            return;
        }

        for (let i = 0; i < this.turretActors.length; i++) {
            this.turretActors[i].hideLevel();
        }
    }

    private addObstacles(): void {

        for (let i = 0; i < GameVars.obstaclesBoard.length; i++) {
            for (let j = 0; j < GameVars.obstaclesBoard[0].length; j++) {

                if (GameVars.obstaclesBoard[i][j] === 10) {
                    let img = new Phaser.GameObjects.Sprite(this.scene, - GameConstants.CELLS_SIZE + GameConstants.CELLS_SIZE * j + GameConstants.CELLS_SIZE / 2, - GameConstants.CELLS_SIZE * 8 + GameConstants.CELLS_SIZE * i + GameConstants.CELLS_SIZE / 2, "texture_atlas_1");
                    this.scene.add.existing(img);
                    img.anims.play("water");
                    this.waterContainer.add(img);

                    let value = this.addWaterEdges(i, j);

                    if ((parseInt(value) >= 3 && parseInt(value) <= 16) || (parseInt(value) >= 26 && parseInt(value) <= 29) || parseInt(value) === 24 || parseInt(value) === 32 || parseInt(value) === 35) {
                        img = new Phaser.GameObjects.Sprite(this.scene, - GameConstants.CELLS_SIZE + GameConstants.CELLS_SIZE * j + GameConstants.CELLS_SIZE / 2, - GameConstants.CELLS_SIZE * 8 + GameConstants.CELLS_SIZE * i + GameConstants.CELLS_SIZE / 2, "texture_atlas_4", "water_edge_" + value);
                        this.scene.add.existing(img);
                        this.waterContainer.add(img);
                    }   
                    
                } else if (GameVars.obstaclesBoard[i][j] !== 0) {
                    let img = new Phaser.GameObjects.Image(this.scene, - GameConstants.CELLS_SIZE + GameConstants.CELLS_SIZE * j + GameConstants.CELLS_SIZE / 2, - GameConstants.CELLS_SIZE * 7 + GameConstants.CELLS_SIZE * i, "texture_atlas_1", "obstacle_0" + GameVars.obstaclesBoard[i][j]);
                    img.setOrigin(.5, 1);
                    this.board.actorsContainer.add(img);
                }

                this.addWaterEdges(i, j);
            }
        }
    }

    private addWaterEdges(i: number, j: number): string {

        let boarderCells = [0, 0, 0, 1, 1, 1, 0, 0];

        if (GameVars.obstaclesBoard[i - 1] && GameVars.obstaclesBoard[i - 1][j] && GameVars.obstaclesBoard[i - 1][j] === 10) {
            boarderCells[0] = 1;
        }

        if (GameVars.obstaclesBoard[i - 1] && GameVars.obstaclesBoard[i - 1][j + 1] && GameVars.obstaclesBoard[i - 1][j + 1] === 10) {
            boarderCells[1] = 1;
        }

        if (GameVars.obstaclesBoard[i] && GameVars.obstaclesBoard[i][j + 1] && GameVars.obstaclesBoard[i][j + 1] === 10) {
            boarderCells[2] = 1;
        }

        if (GameVars.obstaclesBoard[i] && GameVars.obstaclesBoard[i][j - 1] && GameVars.obstaclesBoard[i][j - 1] === 10) {
            boarderCells[6] = 1;
        }

        if (GameVars.obstaclesBoard[i - 1] && GameVars.obstaclesBoard[i - 1][j - 1] && GameVars.obstaclesBoard[i - 1][j - 1] === 10) {
            boarderCells[7] = 1;
        }

        let cellName = "";

        if (boarderCells[0] === 0 && boarderCells[2] === 0 && boarderCells[4] === 0 && boarderCells[6] === 0) {
            cellName += "01";
        } else if (boarderCells[0] === 0 && boarderCells[2] === 0 && boarderCells[6] === 0) {
            cellName += "03";
        } else if (boarderCells[0] === 0 && boarderCells[2] === 0 && boarderCells[4] === 0) {
            cellName += "04";
        } else if (boarderCells[6] === 0 && boarderCells[2] === 0 && boarderCells[4] === 0) {
            cellName += "05";
        } else if (boarderCells[6] === 0 && boarderCells[0] === 0 && boarderCells[4] === 0) {
            cellName += "06";
        } else if (boarderCells[0] === 0 && boarderCells[2] === 0) {
            if (boarderCells[5] === 1) {
                cellName += "07";
            } else {
                cellName += "38";
            }
        } else if (boarderCells[0] === 0 && boarderCells[6] === 0) {
            if (boarderCells[3] === 1) {
                cellName += "08";
            } else {
                cellName += "41";
            }
        } else if (boarderCells[2] === 0 && boarderCells[4] === 0) {
            if (boarderCells[7] === 1) {
                cellName += "09";
            } else {
                cellName += "39";
            }
        } else if (boarderCells[6] === 0 && boarderCells[4] === 0) {
            if (boarderCells[1] === 1) {
                cellName += "10";
            } else {
                cellName += "40";
            }
        } else if (boarderCells[0] === 0 && boarderCells[4] === 0) {
            cellName += "11";
        } else if (boarderCells[2] === 0 && boarderCells[6] === 0) {
            cellName += "12";
        } else if (boarderCells[6] === 0) {
            if (boarderCells[1] === 0 && boarderCells[3] === 0) {
                cellName += "45";
            } else if (boarderCells[1] === 0) {
                cellName += "35";
            } else if (boarderCells[3] === 0) {
                cellName += "36";
            } else {
                cellName += "13";
            }
            
        } else if (boarderCells[0] === 0) {
            if (boarderCells[5] === 0 && boarderCells[3] === 0) {
                cellName += "42";
            } else if (boarderCells[5] === 0) {
                cellName += "30";
            } else if (boarderCells[3] === 0) {
                cellName += "37";
            } else {
                cellName += "14";
            }
        } else if (boarderCells[2] === 0) {
            if (boarderCells[5] === 0 && boarderCells[7] === 0) {
                cellName += "43";
            } else if (boarderCells[5] === 0) {
                cellName += "31";
            } else if (boarderCells[7] === 0) {
                cellName += "32";
            } else {
                cellName += "15";
            }
        } else if (boarderCells[4] === 0) {
            if (boarderCells[1] === 0 && boarderCells[7] === 0) {
                cellName += "44";
            } else if (boarderCells[1] === 0) {
                cellName += "34";
            } else if (boarderCells[7] === 0) {
                cellName += "33";
            } else {
                cellName += "16";
            }
            
        } else if (boarderCells[1] === 0 && boarderCells[3] === 0 && boarderCells[5] === 0 && boarderCells[7] === 0) {
            cellName += "17";
        } else if (boarderCells[3] === 0 && boarderCells[5] === 0 && boarderCells[7] === 0) {
            cellName += "18";
        } else if (boarderCells[5] === 0 && boarderCells[1] === 0 && boarderCells[7] === 0) {
            cellName += "19";
        } else if (boarderCells[3] === 0 && boarderCells[1] === 0 && boarderCells[7] === 0) {
            cellName += "20";
        } else if (boarderCells[3] === 0 && boarderCells[1] === 0 && boarderCells[5] === 0) {
            cellName += "21";
        } else if (boarderCells[3] === 0 && boarderCells[5] === 0) {
            cellName += "22";
        } else if (boarderCells[7] === 0 && boarderCells[5] === 0) {
            cellName += "23";
        } else if (boarderCells[7] === 0 && boarderCells[1] === 0) {
            cellName += "24";
        } else if (boarderCells[3] === 0 && boarderCells[1] === 0) {
            cellName += "25";
        } else if (boarderCells[5] === 0) {
            cellName += "26";
        } else if (boarderCells[7] === 0) {
            cellName += "27";
        } else if (boarderCells[1] === 0) {
            cellName += "28";
        } else if (boarderCells[3] === 0) {
            cellName += "29";
        } else {
            cellName += "02";
        }
        return cellName;
    }

    private drawDebugGeometry(): void {
        
        const path = new Phaser.GameObjects.Graphics(this.scene);

        path.lineStyle(2, 0xFFA500);

        for (let i = 0; i < GameVars.enemiesPathCells.length - 1; i ++) {

            let x = (GameVars.enemiesPathCells[i].c + .5) * GameConstants.CELLS_SIZE;
            let y = (GameVars.enemiesPathCells[i].r + .5) * GameConstants.CELLS_SIZE;
            
            path.moveTo(x, y);

            x = (GameVars.enemiesPathCells[i + 1].c + .5) * GameConstants.CELLS_SIZE;
            y = (GameVars.enemiesPathCells[i + 1].r + .5) * GameConstants.CELLS_SIZE;

            path.lineTo(x, y);

            path.stroke();
        }

        this.board.add(path);
    }

    private onPointerDown(): void {

        if (!GameVars.gameData.tutorialSeen) {
            return;
        }

        if (this.pauseMenu) {
            this.hidePauseMenu();
        } else {
            this.hideRangeCircles();
            BattleScene.currentInstance.hideTurretMenu();
        }
    }

    private createAnimations(): void {

        this.scene.anims.create({ key: "enemy_blob_back", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_blob_back_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_blob_front", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_blob_front_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_blob_left", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_blob_left_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_blob_right", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_blob_right_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});

        this.scene.anims.create({ key: "enemy_flier_back", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_flier_back_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_flier_front", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_flier_front_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_flier_left", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_flier_left_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_flier_right", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_flier_right_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});

        this.scene.anims.create({ key: "enemy_healer_back", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_healer_back_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_healer_front", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_healer_front_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_healer_left", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_healer_left_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_healer_right", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_healer_right_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});

        this.scene.anims.create({ key: "enemy_runner_back", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_runner_back_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE * 2, repeat: -1});
        this.scene.anims.create({ key: "enemy_runner_front", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_runner_front_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE * 2, repeat: -1});
        this.scene.anims.create({ key: "enemy_runner_left", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_runner_left_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE * 2, repeat: -1});
        this.scene.anims.create({ key: "enemy_runner_right", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_runner_right_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE * 2, repeat: -1});

        this.scene.anims.create({ key: "enemy_soldier_back", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_soldier_back_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_soldier_front", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_soldier_front_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_soldier_left", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_soldier_left_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});
        this.scene.anims.create({ key: "enemy_soldier_right", frames: this.scene.anims.generateFrameNames( "texture_atlas_2", { prefix: "enemy_soldier_right_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: GameConstants.ENEMY_FRAMERATE, repeat: -1});

        this.scene.anims.create({ key: "bolt", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "bolt_", start: 0, end: 13, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        this.scene.anims.create({ key: "flame", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "flame_", start: 0, end: 29, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});
        this.scene.anims.create({ key: "plasma_blast", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "plasma_blast_", start: 1, end: 3, zeroPad: 1, suffix: ""}), frameRate: 30, repeat: -1});
        this.scene.anims.create({ key: "heal", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "heal_fx_", start: 1, end: 36, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});

        this.scene.anims.create({ key: "electricity", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "electricity_", start: 0, end: 11, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: -1});
        this.scene.anims.create({ key: "fire", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "fire_", start: 0, end: 23, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: -1});
        this.scene.anims.create({ key: "plasma", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "plasma_", start: 0, end: 17, zeroPad: 2, suffix: ""}), frameRate: 24, repeat: -1});

        this.scene.anims.create({ key: "snow_shoot", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "fx_snow_shoot_", start: 1, end: 16, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});

        this.scene.anims.create({ key: "explosion", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "explossion_", start: 1, end: 28, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});
        
        this.scene.anims.create({ key: "crystal_top", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "crystal_1_top_", start: 1, end: 18, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});

        this.scene.anims.create({ key: "portal_inner_fx", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "portal_inner_fx_", start: 1, end: 13, zeroPad: 4, suffix: ""}), frameRate: 12, repeat: -1});

        this.scene.anims.create({ key: "teleport_in", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "teleport_in_", start: 1, end: 23, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});
        this.scene.anims.create({ key: "teleport_out", frames: this.scene.anims.generateFrameNames( "texture_atlas_3", { prefix: "teleport_out_", start: 1, end: 3, zeroPad: 2, suffix: ""}), frameRate: 60, repeat: 0});

        this.scene.anims.create({ key: "glue_fx", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "fx_snow_shoot_", start: 1, end: 16, zeroPad: 1, suffix: ""}), frameRate: 12, repeat: 0});
        
        this.scene.anims.create({ key: "mine", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "mine_falling_", start: 1, end: 11, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: -1});
        
        this.scene.anims.create({ key: "splat", frames: this.scene.anims.generateFrameNames("texture_atlas_4", { prefix: "death_splash_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 20, hideOnComplete: true});
        this.scene.anims.create({ key: "red-splat", frames: this.scene.anims.generateFrameNames("texture_atlas_4", { prefix: "red_splash_", start: 1, end: 10, zeroPad: 2, suffix: ""}), frameRate: 20, hideOnComplete: true});

        this.scene.anims.create({ key: "electric_damage", frames: this.scene.anims.generateFrameNames( "texture_atlas_4", { prefix: "electric_damage_", start: 0, end: 17, zeroPad: 2, suffix: ""}), frameRate: 30, repeat: 0});

        this.scene.anims.create({ key: "water", frames: this.scene.anims.generateFrameNames( "texture_atlas_1", { prefix: "cell_water_", start: 1, end: 25, zeroPad: 2, suffix: ""}), frameRate: 16, repeat: -1});
    }
}
